import React, { useState } from 'react';
import {
  Target, Lightbulb, MessageSquare, AlertTriangle, User, ChevronLeft, ChevronRight,
  Zap, Users, Shield, BookOpen, Briefcase, CheckSquare, Sparkles, Award
} from 'lucide-react';

const ScenarioOnboarding = ({ scenario, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState('forward'); // for animations
  const [checkedItems, setCheckedItems] = useState({}); // for interactive checklists

  if (!scenario?.onboarding?.steps || scenario.onboarding.steps.length === 0) {
    // If no onboarding data, skip to training
    onComplete();
    return null;
  }

  const steps = scenario.onboarding.steps;
  const totalSteps = steps.length;
  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const getStepIcon = (type) => {
    const icons = {
      goal: Target,
      methods: Lightbulb,
      examples: MessageSquare,
      mistakes: AlertTriangle,
      character: User,
      triggers: Zap,
      lpr_types: Users,
      objections: Shield,
      services: Briefcase,
      laws: BookOpen,
      checklist: CheckSquare,
      tricks: Sparkles,
      context: BookOpen,
      success: Award,
    };
    const Icon = icons[type] || Target;
    return <Icon className="w-8 h-8" />;
  };

  const getStepColor = (type) => {
    const colors = {
      goal: 'blue',
      methods: 'purple',
      examples: 'green',
      mistakes: 'red',
      character: 'orange',
      triggers: 'yellow',
      lpr_types: 'indigo',
      objections: 'pink',
      services: 'cyan',
      laws: 'slate',
      checklist: 'teal',
      tricks: 'violet',
      context: 'gray',
      success: 'emerald',
    };
    return colors[type] || 'blue';
  };

  const handleNext = () => {
    setDirection('forward');
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    setDirection('backward');
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleCheckItem = (stepIndex, itemIndex) => {
    const key = `${stepIndex}-${itemIndex}`;
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isItemChecked = (stepIndex, itemIndex) => {
    const key = `${stepIndex}-${itemIndex}`;
    return checkedItems[key] || false;
  };

  const color = getStepColor(currentStepData.type);
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      dot: 'bg-blue-600',
      dotInactive: 'bg-blue-200',
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700',
      dot: 'bg-purple-600',
      dotInactive: 'bg-purple-200',
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      button: 'bg-green-600 hover:bg-green-700',
      dot: 'bg-green-600',
      dotInactive: 'bg-green-200',
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-600',
      button: 'bg-red-600 hover:bg-red-700',
      dot: 'bg-red-600',
      dotInactive: 'bg-red-200',
    },
    orange: {
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      button: 'bg-orange-600 hover:bg-orange-700',
      dot: 'bg-orange-600',
      dotInactive: 'bg-orange-200',
    },
    yellow: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-600',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      dot: 'bg-yellow-600',
      dotInactive: 'bg-yellow-200',
    },
    indigo: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-600',
      button: 'bg-indigo-600 hover:bg-indigo-700',
      dot: 'bg-indigo-600',
      dotInactive: 'bg-indigo-200',
    },
    pink: {
      bg: 'bg-pink-50',
      text: 'text-pink-600',
      button: 'bg-pink-600 hover:bg-pink-700',
      dot: 'bg-pink-600',
      dotInactive: 'bg-pink-200',
    },
    cyan: {
      bg: 'bg-cyan-50',
      text: 'text-cyan-600',
      button: 'bg-cyan-600 hover:bg-cyan-700',
      dot: 'bg-cyan-600',
      dotInactive: 'bg-cyan-200',
    },
    slate: {
      bg: 'bg-slate-50',
      text: 'text-slate-600',
      button: 'bg-slate-600 hover:bg-slate-700',
      dot: 'bg-slate-600',
      dotInactive: 'bg-slate-200',
    },
    teal: {
      bg: 'bg-teal-50',
      text: 'text-teal-600',
      button: 'bg-teal-600 hover:bg-teal-700',
      dot: 'bg-teal-600',
      dotInactive: 'bg-teal-200',
    },
    violet: {
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      button: 'bg-violet-600 hover:bg-violet-700',
      dot: 'bg-violet-600',
      dotInactive: 'bg-violet-200',
    },
    gray: {
      bg: 'bg-gray-50',
      text: 'text-gray-600',
      button: 'bg-gray-600 hover:bg-gray-700',
      dot: 'bg-gray-600',
      dotInactive: 'bg-gray-200',
    },
    emerald: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      button: 'bg-emerald-600 hover:bg-emerald-700',
      dot: 'bg-emerald-600',
      dotInactive: 'bg-emerald-200',
    },
  };

  const currentColors = colorClasses[color];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Progress Bar */}
        <div className="relative h-2 bg-slate-200 overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full ${currentColors.button} transition-all duration-500 ease-out`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className={`${currentColors.bg} p-6 border-b border-slate-200`}>
          {/* Step Counter */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-600">
              Шаг {currentStep + 1} из {totalSteps}
            </span>
            <div className="flex items-center gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index <= currentStep
                      ? `${currentColors.dot} w-6`
                      : `${currentColors.dotInactive} w-1.5`
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className={`p-3 bg-white rounded-xl shadow-sm ${currentColors.text}`}>
              {getStepIcon(currentStepData.type)}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-1">
                {currentStepData.title}
              </h2>
              <p className="text-sm text-slate-600">
                {scenario.name}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[50vh]">
          <div
            key={currentStep}
            className={`space-y-6 animate-in duration-300 ${
              direction === 'forward'
                ? 'slide-in-from-right'
                : 'slide-in-from-left'
            }`}
          >
            {/* Main content */}
            {currentStepData.content && (
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-700 text-lg leading-relaxed">
                  {currentStepData.content}
                </p>
              </div>
            )}

            {/* Interactive Checklist - for checklist type */}
            {currentStepData.type === 'checklist' && currentStepData.items && currentStepData.items.length > 0 && (
              <div className="space-y-2">
                {currentStepData.items.map((item, index) => {
                  const checked = isItemChecked(currentStep, index);
                  return (
                    <div
                      key={index}
                      onClick={() => toggleCheckItem(currentStep, index)}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 animate-in slide-in-from-left ${
                        checked
                          ? `${currentColors.bg} border-${currentColors.button.split(' ')[0].replace('bg-', 'border-')} shadow-md`
                          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                        checked
                          ? `${currentColors.button} border-transparent`
                          : 'border-slate-300 bg-white'
                      }`}>
                        {checked && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <p className={`flex-1 ${checked ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
                        {item}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Regular Items list - for other types */}
            {currentStepData.type !== 'checklist' && currentStepData.items && currentStepData.items.length > 0 && (
              <div className="space-y-3">
                {currentStepData.items.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-4 ${currentColors.bg} rounded-lg border border-slate-200 animate-in slide-in-from-left duration-300`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full ${currentColors.button} text-white flex items-center justify-center text-sm font-semibold`}>
                      {index + 1}
                    </div>
                    <p className="text-slate-700 flex-1">{item}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Examples */}
            {currentStepData.examples && currentStepData.examples.length > 0 && (
              <div className="space-y-4">
                {currentStepData.examples.map((example, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg animate-in slide-in-from-left duration-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {example.situation && (
                      <div className="text-xs font-semibold text-green-700 uppercase mb-2">
                        {example.situation}
                      </div>
                    )}
                    <p className="text-slate-700 italic">"{example.phrase}"</p>
                  </div>
                ))}
              </div>
            )}

            {/* Mistakes */}
            {currentStepData.mistakes && currentStepData.mistakes.length > 0 && (
              <div className="space-y-3">
                {currentStepData.mistakes.map((mistake, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200 animate-in slide-in-from-left duration-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex-shrink-0 text-red-500 mt-0.5">
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <p className="text-slate-700 flex-1">{mistake}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6 bg-slate-50">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? `${currentColors.dot} w-8`
                    : `${currentColors.dotInactive} w-2 hover:w-4`
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                currentStep === 0
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              Назад
            </button>

            <button
              onClick={handleNext}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-all ${currentColors.button}`}
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  Начать тренировку
                  <Target className="w-5 h-5" />
                </>
              ) : (
                <>
                  Далее
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>

          {/* Skip button */}
          <button
            onClick={onComplete}
            className="w-full mt-3 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            Пропустить онбординг
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioOnboarding;
